/**
 * PivotTable.jsx — v5
 * ─────────────────────────────────────────────────────────────────
 * v5 — performance + server-side pagination
 *  • Server mode (pass `serverRequest`): flat tables fetch ONE page at a
 *    time from /chart-data-page. Search, sort and row count run in
 *    Postgres via Superset; export is generated by the backend.
 *  • Client mode (pass `data`, as before): unchanged behaviour, but
 *      - hover is pure CSS (no React re-render of the whole table)
 *      - cell-bar min/max computed once per column, not once per cell
 *      - pivot built once (was built twice per render)
 *      - search is debounced + memoised
 *      - compact pager instead of one button per page
 *
 * Earlier fixes kept:
 *  Fix 1: Pivot colours — rules use metric name (e.g. "MIN(rn)").
 *  Fix 2: 3-row pivot header (Metric / centername / quarter_label).
 *  Fix 3: Column order from backend columnOrder.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "./api"; // ← your shared axios instance (the one ChartCard uses)

// Endpoints — use the SAME prefix ChartCard uses for "/chart-data".
// If ChartCard calls api.post("/api/chart-data"), change these to "/api/...".
const PAGE_ENDPOINT   = "/chart-data-page";
const EXPORT_ENDPOINT = "/chart-data-export";

const CLIENT_ROWS_PER_PAGE = 500;  // client mode (data already in memory)
const SERVER_PAGE_SIZE     = 100;  // server mode (rows fetched per request)
const SEARCH_DEBOUNCE_MS   = 350;
const MAX_GET_URL_LENGTH   = 3000; // gunicorn rejects request lines > ~4 KB by default

// ── GET with query parameters ────────────────────────────────────
// { sliceId: 42, page: 1, activeFilters: [...] }
//   → ?sliceId=42&page=1&activeFilters=%5B%7B...%7D%5D
// Scalars go as-is; arrays/objects (filters) are JSON-encoded into one param.
// Empty values are left out so the URL stays short.
function toQueryParams(payload) {
  const params = {};
  for (const [key, value] of Object.entries(payload || {})) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value) && value.length === 0) continue;
    params[key] = typeof value === "object" ? JSON.stringify(value) : String(value);
  }
  return params;
}

// Sends GET ?query=params normally. If many filter values would make the URL
// too long, sends the same fields as a POST JSON body instead (backend accepts both).
function chartRequest(endpoint, payload, axiosOptions = {}) {
  const params = toQueryParams(payload);
  const urlLength = endpoint.length + 1 + new URLSearchParams(params).toString().length;
  if (urlLength <= MAX_GET_URL_LENGTH) {
    return api.get(endpoint, { ...axiosOptions, params });
  }
  return api.post(endpoint, payload, axiosOptions);
}

// ── Small hooks ──────────────────────────────────────────────────
function useDebouncedValue(value, delay) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

// ── Row hover / striping via CSS (no React state → no re-render on hover) ─
const TABLE_CSS = `
  .spg-tbl tbody tr.spg-row:nth-child(odd)  { background: #0d1117; }
  .spg-tbl tbody tr.spg-row:nth-child(even) { background: #111820; }
  .spg-tbl tbody tr.spg-row:not(.spg-summary):hover > td { background-color: rgba(31,168,201,0.08); }
  .spg-tbl tbody tr.spg-click { cursor: pointer; }
  .spg-pager-btn:disabled { opacity: .35; cursor: default; }
`;

// ── Excel export (proper .xlsx with bold headers + borders) ─────
// Client mode only. Uses SheetJS loaded from CDN — no npm install needed
function exportToExcel(filename, headers, rows2d) {
  const doExport = (XLSX) => {
    const wsData = [headers, ...rows2d];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    const colCount = headers.length;
    const rowCount = wsData.length;
    const borderStyle = { style: "thin", color: { rgb: "000000" } };
    const border = { top: borderStyle, bottom: borderStyle, left: borderStyle, right: borderStyle };

    for (let R = 0; R < rowCount; R++) {
      for (let C = 0; C < colCount; C++) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellRef]) ws[cellRef] = { v: "", t: "s" };
        ws[cellRef].s = {
          border,
          font: R === 0 ? { bold: true, sz: 11 } : { sz: 10 },
          alignment: { vertical: "center", wrapText: false },
        };
      }
    }

    ws["!cols"] = headers.map((h, ci) => {
      let maxLen = String(h).length;
      for (const r of rows2d) {
        const l = String(r[ci] ?? "").length;
        if (l > maxLen) maxLen = l;
      }
      return { wch: Math.min(Math.max(maxLen + 2, 10), 40) };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, filename.endsWith(".xlsx") ? filename : filename + ".xlsx");
  };

  if (window.XLSX) {
    doExport(window.XLSX);
  } else {
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
    script.onload = () => doExport(window.XLSX);
    script.onerror = () => {
      const escape = (v) => { const s = String(v ?? ""); return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s; };
      const lines = [headers.map(escape).join(","), ...rows2d.map(r => r.map(escape).join(","))];
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      downloadBlob(blob, filename.replace(/\.xlsx$/, ".csv"));
    };
    document.head.appendChild(script);
  }
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Export button component
function ExportBtn({ onClick, busy }) {
  return (
    <button
      onClick={busy ? undefined : onClick}
      disabled={busy}
      title="Export to Excel (.xlsx)"
      style={{
        display: "flex", alignItems: "center", gap: 5,
        padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
        border: "1px solid rgba(31,168,201,0.35)",
        background: "rgba(31,168,201,0.08)", color: "#1FA8C9",
        cursor: busy ? "wait" : "pointer", flexShrink: 0, transition: "all .15s",
        opacity: busy ? 0.6 : 1,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = "rgba(31,168,201,0.18)"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "rgba(31,168,201,0.08)"; }}
    >
      {busy ? "Exporting…" : "⬇ Export xlsx"}
    </button>
  );
}

// ── Image lightbox ────────────────────────────────────────────────
// Rendered via a portal into document.body — dashboard tiles often sit
// inside a transformed container, which breaks position:fixed.
function ImageLightbox({ src, alt, onClose }) {
  if (!src) return null;

  const content = (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 99999,
        background: "rgba(0,0,0,0.82)",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "zoom-out",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ position: "relative", width: "min(480px, 80vw)", height: "min(480px, 80vh)" }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute", top: -14, right: -14,
            width: 34, height: 34, borderRadius: "50%",
            border: "2px solid #fff", background: "#111",
            color: "#fff", fontSize: 18, fontWeight: 700, lineHeight: 1,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 10px rgba(0,0,0,0.6)", zIndex: 2,
          }}
        >
          ✕
        </button>
        <div
          style={{
            width: "100%", height: "100%", background: "#fff", borderRadius: 10,
            boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
          }}
        >
          <img src={src} alt={alt || ""} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

// Search bar component — flexible, takes available space, min 120px max 280px
function SearchBar({ value, onChange, placeholder = "Search..." }) {
  return (
    <div style={{ position: "relative", flex: 1, minWidth: 120, maxWidth: 280 }}>
      <svg style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
                    width: 12, height: 12, opacity: 0.4, pointerEvents: "none" }}
           viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: "100%", boxSizing: "border-box",
          background: "#12151f",
          border: "1px solid rgba(255,255,255,0.09)",
          borderRadius: 6,
          padding: "5px 28px 5px 28px",
          color: "#c9d1d9", fontSize: 11, outline: "none",
          transition: "border-color .15s",
        }}
        onFocus={e => { e.currentTarget.style.borderColor = "rgba(31,168,201,0.45)"; }}
        onBlur={e =>  { e.currentTarget.style.borderColor = "rgba(255,255,255,0.09)"; }}
      />
      {value && (
        <span onClick={() => onChange("")} style={{
          position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
          fontSize: 10, color: "#4a5568", cursor: "pointer", userSelect: "none",
        }}>✕</span>
      )}
    </div>
  );
}

const CURRENCY_COL_PATTERN =
  /amount|net|sales|revenue|value|cost|price|mrp|sell|earning|income|profit|loss/i;

// Quantity/count-type columns must NEVER get the currency symbol (wins over CURRENCY).
const QTY_COL_PATTERN =
  /\bqty\b|\bquantity\b|\bcount\b|\bunits?\b|\bnos?\b|\bpcs\b|\bpieces\b/i;

// Mobile/phone/ID/pincode/invoice/bill-number columns — always plain text
// (no thousands separators, no ₹). Covers names ending in no / num / number,
// e.g. billno, bill_no, voucherno, receipt_number, doc_num.
const ID_COL_PATTERN =
  /\bid\b|_id$|^id_|mobile|phone|contact|tel|fax|whatsapp|pincode|zip|postal|pin_code|\bpin\b|order[_ ]?no|order_id|invoice|^no\.?$|#|(?:no|num|number)\.?$/i;

const HTML_COL_PATTERN = /photo|image|thumbnail|_img$|^img_/i;

function isHtmlValue(val) {
  return typeof val === "string" && /^\s*<[a-z][\s\S]*>/i.test(val);
}

const SUPERSET_BASE_URL = process.env.REACT_APP_SUPERSET_URL || "http://localhost:8088";

function resolveHtmlImageSrc(html) {
  return html.replace(
    /src="(\/static\/[^"]*)"/i,
    (_, path) => `src="${SUPERSET_BASE_URL}${path}"`
  );
}

// ── Conditional formatting ───────────────────────────────────────
// Rules are grouped by column ONCE (groupRulesByColumn) so each cell only
// looks at its own column's rules.
function groupRulesByColumn(rules) {
  const map = new Map();
  for (const r of rules || []) {
    if (!map.has(r.column)) map.set(r.column, []);
    map.get(r.column).push(r);
  }
  return map;
}

function evalConditionalColor(cellValue, matching) {
  if (!matching?.length) return null;
  const num = Number(cellValue);
  for (const rule of matching) {
    const rv = rule.value;
    const op = rule.operator;
    let match = false;
    if (op === "==" || op === "=")  match = num === rv;
    else if (op === "!=")           match = num !== rv;
    else if (op === ">")            match = num > rv;
    else if (op === ">=")           match = num >= rv;
    else if (op === "<")            match = num < rv;
    else if (op === "<=")           match = num <= rv;
    if (match) return rule.color;
  }
  return null;
}

function contrastColor(hex) {
  if (!hex) return "#c9d1d9";
  if (hex.startsWith("rgba") || hex.startsWith("rgb")) {
    const m = hex.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!m) return "#ffffff";
    const lum2 = (0.299 * m[1] + 0.587 * m[2] + 0.114 * m[3]) / 255;
    return lum2 > 0.55 ? "#000000" : "#ffffff";
  }
  const h = hex.replace("#", "");
  const r = parseInt(h.substr(0, 2), 16);
  const g = parseInt(h.substr(2, 2), 16);
  const b = parseInt(h.substr(4, 2), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? "#000000" : "#ffffff";
}

// Single pass min/max — no Math.min(...arr) (that throws on ~100k+ values)
function computeRange(values) {
  let min = Infinity, max = -Infinity, n = 0;
  for (const raw of values) {
    if (raw === null || raw === undefined || raw === "") continue;
    const v = Number(raw);
    if (isNaN(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
    n++;
  }
  return n >= 2 && min !== max ? { min, max } : null;
}

// Magnitude-based colour (show_cell_bars) — light teal (low) → strong teal (high)
function cellBarColor(value, range) {
  if (!range) return null;
  const v = Number(value);
  if (isNaN(v)) return null;
  const pct = (v - range.min) / (range.max - range.min);
  const alpha = 0.12 + pct * 0.65;
  return `rgba(31,168,201,${alpha.toFixed(2)})`;
}

// ── Number / Date formatter ────────────────────────────────────────
function isTimestampMs(n) {
  return Number.isInteger(n) && n > 1_000_000_000_000;
}
// Month-grain dates (1st of month) → "Jan-25"; other dates → YYYY-MM-DD
function fmtDateMs(ms) {
  const d = new Date(ms);
  if (d.getDate() === 1) {
    const mon = d.toLocaleDateString("en-IN", { month: "short" });
    const yr  = String(d.getFullYear()).slice(2);
    return `${mon}-${yr}`;
  }
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function fmtHeaderVal(val) {
  if (val === null || val === undefined || val === "") return "";
  const n = Number(val);
  if (!isNaN(n) && isTimestampMs(n)) return fmtDateMs(n);
  return String(val);
}

// One shared formatter instance (creating Intl.NumberFormat per cell is slow)
const NUM_FMT = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 0 });

// ── Table-cell dates ──────────────────────────────────────────────
// Superset sends dates as epoch milliseconds for midnight UTC, so they are
// read with UTC getters (never shifts a day, whatever the viewer's timezone).
// A column shows "Jan-25" style ONLY when the whole column is month-grain
// (every value is the 1st of a month); any other date column shows
// YYYY-MM-DD for every row — same as Superset's table.
function fmtDateISO(ms) {
  const d = new Date(Number(ms));
  if (isNaN(d.getTime())) return String(ms);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function fmtDateMonth(ms) {
  const d = new Date(Number(ms));
  if (isNaN(d.getTime())) return String(ms);
  return `${MONTHS_SHORT[d.getUTCMonth()]}-${String(d.getUTCFullYear()).slice(2)}`;
}
function fmtDateCell(val, monthGrain) {
  if (val === null || val === undefined || val === "") return "";
  const n = Number(val);
  if (!isTimestampMs(n)) return String(val);          // already a string date → show as-is
  return monthGrain ? fmtDateMonth(n) : fmtDateISO(n);
}

function fmtTableNum(val, colName = "") {
  if (val === null || val === undefined || val === "") return "";
  if (colName && ID_COL_PATTERN.test(colName)) return String(val);
  const n = Number(val);
  if (isNaN(n)) return String(val);
  if (isTimestampMs(n)) return fmtDateISO(n);
  const formatted = NUM_FMT.format(n);
  if (colName && CURRENCY_COL_PATTERN.test(colName) && !QTY_COL_PATTERN.test(colName)) return `₹${formatted}`;
  return formatted;
}

// Excel export value: numbers stay numbers, dates → string, IDs → text
function exportCellValue(val, colName = "") {
  if (val === null || val === undefined) return "";
  if (colName && ID_COL_PATTERN.test(colName)) return String(val);
  const n = Number(val);
  if (!isNaN(n) && isTimestampMs(n)) return fmtDateISO(n);
  return val;
}

// ── Quarter/period sorter ─────────────────────────────────────────
function sortPeriodValues(vals) {
  const quarterRe   = /^(\d{4})-Q(\d)$/;
  const yearMonthRe = /^(\d{4})-(\d{2})$/;
  const monYearRe   = /^([A-Za-z]{3})-(\d{2})$/;
  const MONTHS = { jan:1,feb:2,mar:3,apr:4,may:5,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12 };

  const toNum = (v) => {
    let m;
    if ((m = String(v).match(quarterRe)))   return Number(m[1]) * 10 + Number(m[2]);
    if ((m = String(v).match(yearMonthRe))) return Number(m[1]) * 100 + Number(m[2]);
    if ((m = String(v).match(monYearRe))) {
      const mo = MONTHS[m[1].toLowerCase()] || 0;
      return (Number(m[2]) + 2000) * 100 + mo;
    }
    return null;
  };

  const allNumeric = vals.every(v => toNum(v) !== null);
  if (!allNumeric) return [...vals].sort((a, b) => String(a).localeCompare(String(b)));
  return [...vals].sort((a, b) => toNum(a) - toNum(b));
}

function normKey(s) {
  return String(s ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

// ── Pivot builder ─────────────────────────────────────────────────
function buildPivot(rows, groupbyRows, groupbyColumns, metricKeys, columnOrder) {
  if (!rows?.length || !groupbyColumns?.length || !metricKeys?.length) return null;
  const rowDims = groupbyRows || [];
  const colDims = groupbyColumns || [];
  const metrics = metricKeys || [];

  // Step 1: unique top-level values in insertion order
  const topDimSeen = new Set();
  const topDimVals = [];
  for (const r of rows) {
    const v = r[colDims[0]];
    if (v != null && !topDimSeen.has(v)) { topDimSeen.add(v); topDimVals.push(v); }
  }

  // Step 2: order top-level by columnOrder (normalised match) or alphabetically
  let sortedTopVals;
  if (columnOrder?.length) {
    const orderMap = new Map(columnOrder.map((v, i) => [normKey(v), i]));
    sortedTopVals = [...topDimVals].sort((a, b) => {
      const ia = orderMap.has(normKey(a)) ? orderMap.get(normKey(a)) : 9999;
      const ib = orderMap.has(normKey(b)) ? orderMap.get(normKey(b)) : 9999;
      return ia - ib;
    });
  } else {
    sortedTopVals = [...topDimVals].sort((a, b) => String(a).localeCompare(String(b)));
  }

  // Step 3: sub-dim combos per top value — ONE pass over rows (was one pass per top value)
  const colCombos = [];
  if (colDims.length === 1) {
    for (const topVal of sortedTopVals) colCombos.push([topVal]);
  } else {
    const subByTop = new Map(); // topVal -> { seen:Set, list:[] }
    for (const r of rows) {
      const top = r[colDims[0]];
      if (top == null) continue;
      let bucket = subByTop.get(top);
      if (!bucket) { bucket = { seen: new Set(), list: [] }; subByTop.set(top, bucket); }
      const subArr = colDims.slice(1).map(d => r[d] ?? "");
      const subKey = subArr.join("||");
      if (!bucket.seen.has(subKey)) { bucket.seen.add(subKey); bucket.list.push(subArr); }
    }
    for (const topVal of sortedTopVals) {
      const subVals = subByTop.get(topVal)?.list || [];
      const sortedSubFirst = sortPeriodValues(subVals.map(s => s[0]));
      const subRankMap = new Map(sortedSubFirst.map((v, i) => [v, i]));
      subVals.sort((a, b) => (subRankMap.get(a[0]) ?? 999) - (subRankMap.get(b[0]) ?? 999));
      for (const subArr of subVals) colCombos.push([topVal, ...subArr]);
    }
  }

  const rowMap = new Map();
  for (const row of rows) {
    const rowKey = rowDims.map((d) => row[d] ?? "").join("||");
    let entry = rowMap.get(rowKey);
    if (!entry) {
      entry = { dimValues: rowDims.map((d) => row[d] ?? ""), metricMap: {} };
      rowMap.set(rowKey, entry);
    }
    const colKey = colDims.map((d) => row[d] ?? "").join("||");
    for (const mk of metrics) {
      entry.metricMap[colKey + "||" + mk] = row[mk];
    }
  }

  // Lower-cased search text per pivot row (built once, used by search)
  const allRows = [...rowMap.values()];
  for (const e of allRows) e.searchText = e.dimValues.join("\u0001").toLowerCase();

  return { rowDims, colDims, colCombos, metrics, rowMap, allRows };
}

// ── Pager — compact, works for 10 pages or 1,000 ─────────────────
function Pager({ total, page, pageSize, setPage, loading, isEstimate }) {
  const [jump, setJump] = useState("");
  if (!total) return null;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = page * pageSize + 1;
  const end   = Math.min((page + 1) * pageSize, total);
  const go = (p) => setPage(Math.min(Math.max(p, 0), totalPages - 1));

  const btn = {
    padding: "2px 8px", borderRadius: 6, fontSize: 11,
    border: "1px solid rgba(255,255,255,0.1)", background: "transparent",
    color: "#94a3b8", cursor: "pointer",
  };

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      borderTop: "1px solid rgba(255,255,255,0.07)", background: "#12151f",
      padding: "5px 10px", gap: 8, minHeight: 34, flexShrink: 0, flexWrap: "wrap",
    }}>
      <span style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap" }}>
        {totalPages > 1
          ? <>{start.toLocaleString()}–{end.toLocaleString()} of {isEstimate ? "≥ " : ""}{total.toLocaleString()} rows</>
          : <>{total.toLocaleString()} rows</>}
        {loading && <span style={{ marginLeft: 8, color: "#1FA8C9" }}>loading…</span>}
      </span>

      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button className="spg-pager-btn" style={btn} disabled={page === 0} onClick={() => go(0)} title="First page">«</button>
          <button className="spg-pager-btn" style={btn} disabled={page === 0} onClick={() => go(page - 1)} title="Previous page">‹</button>
          <span style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap" }}>
            Page{" "}
            <input
              value={jump !== "" ? jump : String(page + 1)}
              onChange={e => setJump(e.target.value.replace(/[^\d]/g, ""))}
              onBlur={() => { if (jump) go(Number(jump) - 1); setJump(""); }}
              onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
              style={{
                width: 42, textAlign: "center", fontSize: 11, padding: "2px 4px",
                background: "#0d1117", color: "#c9d1d9", borderRadius: 4,
                border: "1px solid rgba(255,255,255,0.12)", outline: "none",
              }}
            />{" "}
            of {totalPages.toLocaleString()}
          </span>
          <button className="spg-pager-btn" style={btn} disabled={page >= totalPages - 1} onClick={() => go(page + 1)} title="Next page">›</button>
          <button className="spg-pager-btn" style={btn} disabled={page >= totalPages - 1 || isEstimate} onClick={() => go(totalPages - 1)} title="Last page">»</button>
        </div>
      )}
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────
const S = {
  wrapper:   { overflowX: "auto", overflowY: "auto", maxHeight: "100%", fontSize: 12,
               fontFamily: "'Inter','Roboto',sans-serif" },
  table:     { borderCollapse: "collapse", width: "100%", minWidth: 400 },

  thLabel:   { padding: "3px 8px", textAlign: "left", fontWeight: 400, fontSize: 10,
               whiteSpace: "nowrap", borderBottom: "1px solid rgba(255,255,255,0.06)",
               borderRight: "1px solid rgba(255,255,255,0.04)", background: "#0d1117",
               color: "#4a5568", fontStyle: "italic" },
  thLabelDim:{ padding: "3px 8px", textAlign: "left", fontWeight: 400, fontSize: 10,
               whiteSpace: "nowrap", borderBottom: "1px solid rgba(255,255,255,0.06)",
               borderRight: "1px solid rgba(255,255,255,0.06)", background: "#0d1117",
               color: "#4a5568", fontStyle: "italic" },

  thBase:    { padding: "5px 10px", textAlign: "left", fontWeight: 600, fontSize: 11,
               whiteSpace: "nowrap", borderBottom: "1px solid rgba(255,255,255,0.10)",
               borderRight: "1px solid rgba(255,255,255,0.06)", background: "#161b22",
               color: "#8b9dc3", position: "sticky", top: 0, zIndex: 2 },
  thGroup:   { padding: "5px 10px", textAlign: "center", fontWeight: 700, fontSize: 12,
               whiteSpace: "nowrap", borderBottom: "1px solid rgba(255,255,255,0.10)",
               borderRight: "2px solid rgba(31,168,201,0.25)", background: "#0d1a26",
               color: "#1FA8C9", position: "sticky", top: 0, zIndex: 2 },
  thSub:     { padding: "4px 8px", textAlign: "center", fontWeight: 600, fontSize: 11,
               whiteSpace: "nowrap", borderBottom: "1px solid rgba(255,255,255,0.10)",
               borderRight: "1px solid rgba(255,255,255,0.06)", background: "#12171f",
               color: "#5a9fbf", position: "sticky", zIndex: 2 },
  thDim:     { padding: "4px 8px", textAlign: "left", fontWeight: 600, fontSize: 11,
               color: "#6b7fa8", background: "#161b22",
               borderBottom: "1px solid rgba(255,255,255,0.08)",
               borderRight: "1px solid rgba(255,255,255,0.06)",
               position: "sticky", top: 0, zIndex: 3 },
  td:        { padding: "4px 10px", borderBottom: "1px solid rgba(255,255,255,0.05)",
               borderRight: "1px solid rgba(255,255,255,0.04)", color: "#c9d1d9",
               whiteSpace: "nowrap" },
  tdNum:     { padding: "4px 10px", textAlign: "right",
               borderBottom: "1px solid rgba(255,255,255,0.05)",
               borderRight: "1px solid rgba(255,255,255,0.04)",
               fontVariantNumeric: "tabular-nums" },
  tdEmpty:   { padding: "4px 10px", textAlign: "right",
               borderBottom: "1px solid rgba(255,255,255,0.05)",
               borderRight: "1px solid rgba(255,255,255,0.04)" },
  // background removed — rows are striped by TABLE_CSS so hover can show through
  tdRowDim:  { padding: "4px 10px", borderBottom: "1px solid rgba(255,255,255,0.05)",
               borderRight: "1px solid rgba(255,255,255,0.08)", color: "#e6edf3",
               fontWeight: 500, whiteSpace: "nowrap" },
  toolbar:   { display: "flex", alignItems: "center", gap: 8,
               padding: "6px 8px", borderBottom: "1px solid rgba(255,255,255,0.05)",
               background: "#0d1117", flexShrink: 0 },
  message:   { color: "#8b8fa8", padding: 16, fontSize: 12, textAlign: "center" },
};

// ── PIVOT TABLE (client-side; pivot needs the full aggregated result) ──
function ProperPivotTable({ pivot, height, conditionalFormatting, showCellBars, onRowClick }) {
  const [page, setPage]               = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.trim().toLowerCase(), SEARCH_DEBOUNCE_MS);

  const rulesByCol = useMemo(() => groupRulesByColumn(conditionalFormatting), [conditionalFormatting]);

  const topGroups = useMemo(() => {
    if (pivot.colDims.length < 2) return null;
    const seen = new Map();
    for (const combo of pivot.colCombos) {
      seen.set(combo[0], (seen.get(combo[0]) || 0) + pivot.metrics.length);
    }
    return [...seen.entries()];
  }, [pivot]);

  // One min/max per metric across the whole pivot (computed once)
  const metricRanges = useMemo(() => {
    if (!showCellBars) return {};
    const map = {};
    for (const mk of pivot.metrics) {
      const suffix = "||" + mk;
      const vals = [];
      for (const entry of pivot.allRows) {
        for (const k in entry.metricMap) if (k.endsWith(suffix)) vals.push(entry.metricMap[k]);
      }
      map[mk] = computeRange(vals);
    }
    return map;
  }, [pivot, showCellBars]);

  const filteredRows = useMemo(
    () => (search ? pivot.allRows.filter(e => e.searchText.includes(search)) : pivot.allRows),
    [pivot, search]
  );

  useEffect(() => { setPage(0); }, [pivot, search]);

  const total    = filteredRows.length;
  const pageRows = filteredRows.slice(page * CLIENT_ROWS_PER_PAGE, (page + 1) * CLIENT_ROWS_PER_PAGE);

  const rowHeight  = 28;
  const LABEL_ROWS = pivot.colDims.length;

  const handlePivotExport = () => {
    const headers = [
      ...pivot.rowDims,
      ...pivot.colCombos.flatMap(combo =>
        pivot.metrics.map(mk =>
          pivot.metrics.length > 1
            ? combo.map(fmtHeaderVal).join(" | ") + " | " + mk
            : combo.map(fmtHeaderVal).join(" | ")
        )
      ),
    ];
    const rows2d = filteredRows.map(entry => [
      ...entry.dimValues,
      ...pivot.colCombos.flatMap(combo =>
        pivot.metrics.map(mk => exportCellValue(entry.metricMap[combo.join("||") + "||" + mk], mk))
      ),
    ]);
    exportToExcel("pivot_table.xlsx", headers, rows2d);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: height || "100%", overflow: "hidden" }}>
      <style>{TABLE_CSS}</style>
      <div style={S.toolbar}>
        <SearchBar value={searchInput} onChange={setSearchInput} placeholder="Search rows..." />
        {search && (
          <span style={{ fontSize: 10, color: "#4a5568", whiteSpace: "nowrap" }}>
            {total} result{total !== 1 ? "s" : ""}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <ExportBtn onClick={handlePivotExport} />
      </div>
      <div style={{ ...S.wrapper, flex: 1 }}>
        <table className="spg-tbl" style={S.table}>
          <thead>
            {/* Row 0 — "Metric" label row */}
            <tr>
              <td colSpan={pivot.rowDims.length}
                  style={{ ...S.thLabelDim, position: "sticky", top: 0, zIndex: 3 }}>
                Metric
              </td>
              <td colSpan={pivot.colCombos.length * pivot.metrics.length}
                  style={{ ...S.thLabel, position: "sticky", top: 0, zIndex: 2 }}>
                {pivot.metrics.join(", ")}
              </td>
            </tr>

            {/* Row 1 — top-level column groups */}
            {pivot.colDims.length >= 2 && topGroups && (
              <tr>
                {pivot.rowDims.map((dim, i) => (
                  <th key={`rh-${i}`} style={{ ...S.thDim, top: `${rowHeight}px` }} rowSpan={LABEL_ROWS}>
                    {dim}
                  </th>
                ))}
                {topGroups.map(([val, span], gi) => (
                  <th key={String(val)} colSpan={span}
                      style={{ ...S.thGroup, top: `${rowHeight}px`,
                               borderLeft: gi === 0 ? "2px solid rgba(31,168,201,0.3)" : undefined }}>
                    {fmtHeaderVal(val)}
                  </th>
                ))}
              </tr>
            )}

            {/* Rows 2..N — sub-dim headers */}
            {pivot.colDims.length >= 2 &&
              Array.from({ length: pivot.colDims.length - 1 }, (_, dimIdx) => (
                <tr key={`sub-${dimIdx}`}>
                  {pivot.colCombos.flatMap((combo) =>
                    pivot.metrics.map((mk) => (
                      <th key={combo.join("||") + mk}
                          style={{ ...S.thSub, top: `${(dimIdx + 2) * rowHeight}px` }}>
                        {fmtHeaderVal(combo[dimIdx + 1])}
                        {pivot.metrics.length > 1 && (
                          <div style={{ fontSize: 9, color: "#4a8fa8", fontWeight: 400 }}>{mk}</div>
                        )}
                      </th>
                    ))
                  )}
                </tr>
              ))}

            {/* Single-level column dim */}
            {pivot.colDims.length === 1 && (
              <tr>
                {pivot.rowDims.map((dim, i) => (
                  <th key={`rh-${i}`} style={S.thBase}>{dim}</th>
                ))}
                {pivot.colCombos.flatMap((combo) =>
                  pivot.metrics.map((mk) => (
                    <th key={combo.join("||") + mk} style={{ ...S.thBase, textAlign: "center" }}>
                      {fmtHeaderVal(combo[0])}
                      {pivot.metrics.length > 1 && (
                        <div style={{ fontSize: 9, color: "#5a9fbf" }}>{mk}</div>
                      )}
                    </th>
                  ))
                )}
              </tr>
            )}
          </thead>

          <tbody>
            {pageRows.length === 0 && (
              <tr><td colSpan={pivot.rowDims.length + pivot.colCombos.length * pivot.metrics.length} style={S.message}>
                No matching rows
              </td></tr>
            )}
            {pageRows.map((entry, ri) => (
              <tr
                key={ri}
                className={`spg-row${onRowClick ? " spg-click" : ""}`}
                onClick={() => {
                  if (!onRowClick) return;
                  const col = pivot.rowDims[0];
                  const val = entry.dimValues[0];
                  if (col && val) onRowClick(col, val);
                }}
              >
                {entry.dimValues.map((val, di) => (
                  <td key={`d-${di}`} style={S.tdRowDim}>{val}</td>
                ))}
                {pivot.colCombos.flatMap((combo) =>
                  pivot.metrics.map((mk) => {
                    const cellKey = combo.join("||") + "||" + mk;
                    const raw = entry.metricMap[cellKey];
                    if (raw === null || raw === undefined || raw === "") {
                      return <td key={cellKey} style={S.tdEmpty} />;
                    }
                    const cfColor  = evalConditionalColor(raw, rulesByCol.get(mk));
                    const barColor = (!cfColor && showCellBars) ? cellBarColor(raw, metricRanges[mk]) : null;
                    const bgColor  = cfColor || barColor;
                    const cellStyle = bgColor
                      ? { ...S.tdNum, background: bgColor, color: contrastColor(bgColor), fontWeight: cfColor ? 600 : 400 }
                      : { ...S.tdNum, color: "#1FA8C9" };
                    return <td key={cellKey} style={cellStyle}>{fmtTableNum(raw, mk)}</td>;
                  })
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager total={total} page={page} pageSize={CLIENT_ROWS_PER_PAGE} setPage={setPage} />
    </div>
  );
}

// ── FLAT TABLE ────────────────────────────────────────────────────
// Client mode : `data` holds every row (old behaviour, now faster).
// Server mode : `serverRequest` is the body ChartCard sends to /chart-data;
//               this component fetches one page at a time.
function FlatTable({
  data, serverRequest, height, metricKeys, columnOrder, colnames,
  conditionalFormatting, showCellBars, onRowClick,
}) {
  const serverMode = !!serverRequest;
  const pageSize   = serverMode ? SERVER_PAGE_SIZE : CLIENT_ROWS_PER_PAGE;

  const [page, setPage]               = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.trim(), SEARCH_DEBOUNCE_MS);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState(null); // 'asc' | 'desc' | null
  const [lightbox, setLightbox]   = useState(null);
  const [exporting, setExporting] = useState(false);

  // ── Server-mode state ────────────────────────────────────────────
  const [srv, setSrv] = useState({ rows: [], colnames: [], coltypes: [], total: 0, isEstimate: false, summary: null });
  const [loading, setLoading]       = useState(serverMode);
  const [error, setError]           = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const reqRef = useRef(serverRequest);
  reqRef.current = serverRequest;
  // Stable string key: only changes when filters/date/slice really change
  const requestKey = serverMode ? JSON.stringify(serverRequest) : "";
  const filterKey  = `${requestKey}|${search}|${sortCol}|${sortDir}`;
  const lastFilterKey = useRef(filterKey);

  useEffect(() => {
    if (!serverMode) return undefined;

    // Filters/search/sort changed while not on page 0 → jump to page 0 first;
    // this effect re-runs for page 0 and fetches once (no wasted request).
    if (lastFilterKey.current !== filterKey) {
      lastFilterKey.current = filterKey;
      if (page !== 0) { setPage(0); return undefined; }
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    // GET /chart-data-page?sliceId=..&page=..&pageSize=100&search=..&sortCol=..&sortDir=..
    // Only THIS page is requested — page 2 is fetched when the user clicks it.
    chartRequest(
      PAGE_ENDPOINT,
      { ...reqRef.current, page, pageSize: SERVER_PAGE_SIZE, search, sortCol, sortDir },
      { signal: controller.signal, timeout: 90000 }
    )
      .then(res => {
        const d = res.data || {};
        setSrv({
          rows:       d.data || [],
          colnames:   d.colnames || [],
          coltypes:   d.coltypes || [],
          total:      Number(d.total) || 0,
          isEstimate: !!d.totalIsEstimate,
          summary:    d.summary || null,
        });
      })
      .catch(err => {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        setError(err?.response?.data?.error || err?.message || "Failed to load data");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [serverMode, filterKey, page, reloadTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Client-mode: split real rows from the totals row ─────────────
  const { clientRows, clientSummary } = useMemo(() => {
    if (serverMode) return { clientRows: [], clientSummary: [] };
    const rows = [], sums = [];
    for (const r of data || []) (r && r.__summary__ ? sums : rows).push(r);
    return { clientRows: rows, clientSummary: sums };
  }, [serverMode, data]);

  const sampleRows = serverMode ? srv.rows : clientRows;

  // Column order: backend columnOrder → colnames → keys of first row
  const columns = useMemo(() => {
    let dataKeys;
    if (serverMode && srv.colnames.length) dataKeys = srv.colnames;
    else if (sampleRows.length)            dataKeys = Object.keys(sampleRows[0]);
    else                                   dataKeys = colnames || [];
    dataKeys = dataKeys.filter(k => k !== "__summary__");

    const preferred = columnOrder?.length ? columnOrder : colnames?.length ? colnames : [];
    if (!preferred.length) return dataKeys;

    const dataKeyByNorm = new Map(dataKeys.map(k => [normKey(k), k]));
    const ordered = [];
    const used = new Set();
    for (const p of preferred) {
      const match = dataKeys.includes(p) ? p : dataKeyByNorm.get(normKey(p));
      if (match && !used.has(match)) { ordered.push(match); used.add(match); }
    }
    return [...ordered, ...dataKeys.filter((c) => !used.has(c))];
  }, [serverMode, srv.colnames, sampleRows, columnOrder, colnames]);

  const metricSet = useMemo(() => new Set(metricKeys || []), [metricKeys]);

  // Superset column types (0 = NUMERIC, 1 = STRING, 2 = TEMPORAL) when known
  const typeByCol = useMemo(() => {
    const m = new Map();
    if (serverMode && srv.coltypes.length === srv.colnames.length) {
      srv.colnames.forEach((c, i) => m.set(c, srv.coltypes[i]));
    }
    return m;
  }, [serverMode, srv.colnames, srv.coltypes]);

  // Date columns: every value is an epoch-ms timestamp (or Superset says TEMPORAL).
  // monthCols: date columns where EVERY value is the 1st of a month → "Jan-25".
  const { dateCols, monthCols } = useMemo(() => {
    const dates = new Set(), months = new Set();
    for (const col of columns) {
      if (metricSet.has(col) || ID_COL_PATTERN.test(col)) continue;
      let seen = 0, allTs = true, allFirst = true;
      const distinct = new Set();
      for (const r of sampleRows) {
        const v = r[col];
        if (v === null || v === undefined || v === "") continue;
        const n = Number(v);
        if (!isTimestampMs(n)) { allTs = false; break; }
        seen++;
        if (allFirst) {
          if (new Date(n).getUTCDate() !== 1) allFirst = false;
          else distinct.add(n);
        }
      }
      if (!seen || !allTs) {
        if (typeByCol.get(col) === 2 && !seen) dates.add(col);   // temporal but empty on this page
        continue;
      }
      dates.add(col);
      if (allFirst && distinct.size >= 2) months.add(col);
    }
    return { dateCols: dates, monthCols: months };
  }, [columns, sampleRows, metricSet, typeByCol]);

  // Numeric columns. Server mode uses Superset coltypes (0 = NUMERIC) so the
  // answer doesn't change from page to page; falls back to sampling.
  // Date and ID columns are never numeric.
  const numericCols = useMemo(() => {
    const s = new Set(metricSet);
    for (const col of columns) {
      if (s.has(col)) continue;
      if (ID_COL_PATTERN.test(col) || dateCols.has(col)) continue;
      if (typeByCol.has(col)) {
        if (typeByCol.get(col) === 0) s.add(col);
        continue;
      }
      const sample = sampleRows.slice(0, 20).map((r) => r[col]).filter((v) => v != null);
      if (sample.length && sample.every((v) => !isNaN(Number(v)))) s.add(col);
    }
    return s;
  }, [columns, sampleRows, metricSet, typeByCol, dateCols]);

  // Text shown in a cell for date columns (also used by search + export)
  const displayDate = (col, val) => fmtDateCell(val, monthCols.has(col));

  const rulesByCol = useMemo(() => groupRulesByColumn(conditionalFormatting), [conditionalFormatting]);

  // ── Client-mode search (debounced, lower-cased text built once) ──
  const hasSearch = !serverMode && !!search;
  const haystacks = useMemo(() => {
    if (!hasSearch) return null;
    // Dates are searched by what the user sees (e.g. "2025-01-01"), not the raw timestamp
    return clientRows.map(r => columns.map(c =>
      dateCols.has(c) ? fmtDateCell(r[c], monthCols.has(c)) : String(r[c] ?? "")
    ).join("\u0001").toLowerCase());
  }, [hasSearch, clientRows, columns, dateCols, monthCols]);

  const filteredRows = useMemo(() => {
    if (serverMode) return srv.rows;
    if (!hasSearch || !haystacks) return clientRows;
    const q = search.toLowerCase();
    const out = [];
    for (let i = 0; i < clientRows.length; i++) if (haystacks[i].includes(q)) out.push(clientRows[i]);
    return out;
  }, [serverMode, srv.rows, hasSearch, haystacks, clientRows, search]);

  // ── Client-mode sort (numeric columns) ───────────────────────────
  const sortedRows = useMemo(() => {
    if (serverMode || !sortCol || !sortDir) return filteredRows;
    const rows = [...filteredRows];
    rows.sort((a, b) => {
      const av = Number(a[sortCol]);
      const bv = Number(b[sortCol]);
      const aNaN = isNaN(av), bNaN = isNaN(bv);
      if (aNaN && bNaN) return 0;
      if (aNaN) return 1;
      if (bNaN) return -1;
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return rows;
  }, [serverMode, filteredRows, sortCol, sortDir]);

  // Client mode: back to page 0 when data/search/sort change
  useEffect(() => { if (!serverMode) setPage(0); }, [serverMode, data, search, sortCol, sortDir]);

  // Cell-bar ranges: computed once per column (server mode → current page)
  const colRanges = useMemo(() => {
    if (!showCellBars) return {};
    const src = serverMode ? srv.rows : clientRows;
    const map = {};
    for (const col of columns) {
      if (numericCols.has(col)) map[col] = computeRange(src.map(r => r[col]));
    }
    return map;
  }, [showCellBars, serverMode, srv.rows, clientRows, columns, numericCols]);

  const total = serverMode ? srv.total : filteredRows.length;
  const pageDataRows = serverMode
    ? srv.rows
    : sortedRows.slice(page * pageSize, (page + 1) * pageSize);
  const summaryRows = serverMode
    ? (srv.summary ? [{ __summary__: true, ...srv.summary }] : [])
    : clientSummary;
  const pageRows = [...pageDataRows, ...summaryRows]; // totals pinned on every page

  // Client mode sorts numbers and dates (dates sort correctly by their timestamp)
  const isSortable = (col) => serverMode || numericCols.has(col) || dateCols.has(col);
  const toggleSort = (col) => {
    if (!isSortable(col)) return;
    if (sortCol !== col) { setSortCol(col); setSortDir("asc"); }
    else if (sortDir === "asc") setSortDir("desc");
    else { setSortCol(null); setSortDir(null); }
  };

  // ── Export ───────────────────────────────────────────────────────
  const handleExport = async () => {
    if (!serverMode) {
      const rows2d = [...sortedRows, ...clientSummary].map(row => columns.map(col =>
        dateCols.has(col) ? displayDate(col, row[col]) : exportCellValue(row[col], col)
      ));
      exportToExcel("table_data.xlsx", columns, rows2d);
      return;
    }
    setExporting(true);
    try {
      const res = await chartRequest(
        EXPORT_ENDPOINT,
        { ...reqRef.current, search, sortCol, sortDir },
        { responseType: "blob", timeout: 300000 }
      );
      const ct  = String(res.headers?.["content-type"] || "");
      const ext = ct.includes("csv") ? "csv" : "xlsx";
      downloadBlob(res.data, `table_data.${ext}`);
    } catch (err) {
      let msg = err?.message || "Export failed";
      try {                                  // error body arrives as a Blob
        const txt = await err?.response?.data?.text?.();
        if (txt) msg = JSON.parse(txt).error || msg;
      } catch (_) { /* ignore */ }
      alert(msg);
    } finally {
      setExporting(false);
    }
  };

  const handlePhotoCellClick = (e) => {
    const img = e.target.closest("img");
    if (img) setLightbox({ src: img.src, alt: img.alt });
  };

  // Client mode with no data at all → same as before
  if (!serverMode && !clientRows.length && !clientSummary.length) {
    return <div style={S.message}>No data</div>;
  }

  const firstLoad = serverMode && loading && !srv.rows.length && !srv.colnames.length;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: height || "100%", overflow: "hidden" }}>
      <style>{TABLE_CSS}</style>
      <div style={S.toolbar}>
        <SearchBar value={searchInput} onChange={setSearchInput}
                   placeholder={serverMode ? "Search all rows..." : "Search table..."} />
        {search && !loading && (
          <span style={{ fontSize: 10, color: "#4a5568", whiteSpace: "nowrap" }}>
            {total.toLocaleString()} result{total !== 1 ? "s" : ""}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <ExportBtn onClick={handleExport} busy={exporting} />
      </div>

      {error ? (
        <div style={S.message}>
          {error}{" "}
          <button onClick={() => setReloadTick(t => t + 1)}
                  style={{ marginLeft: 8, fontSize: 11, color: "#1FA8C9", background: "none",
                           border: "1px solid rgba(31,168,201,0.35)", borderRadius: 6,
                           padding: "2px 8px", cursor: "pointer" }}>
            Retry
          </button>
        </div>
      ) : firstLoad ? (
        <div style={S.message}>Loading…</div>
      ) : (
        <div style={{ ...S.wrapper, flex: 1, opacity: serverMode && loading ? 0.55 : 1, transition: "opacity .15s" }}>
          <table className="spg-tbl" style={S.table}>
            <thead>
              <tr>
                {columns.map((col) => {
                  const sortable = isSortable(col);
                  const active   = sortCol === col;
                  return (
                    <th key={col} onClick={() => toggleSort(col)}
                        style={{ ...S.thBase, cursor: sortable ? "pointer" : "default", userSelect: "none" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        {col}
                        {sortable && (
                          <span style={{ display: "inline-flex", flexDirection: "column", lineHeight: 0, marginLeft: 1 }}>
                            <span style={{ fontSize: 8, lineHeight: "8px",
                                           color: active && sortDir === "asc" ? "#1FA8C9" : "#4a5568" }}>▲</span>
                            <span style={{ fontSize: 8, lineHeight: "8px",
                                           color: active && sortDir === "desc" ? "#1FA8C9" : "#4a5568" }}>▼</span>
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {pageDataRows.length === 0 && !loading && (
                <tr><td colSpan={Math.max(columns.length, 1)} style={S.message}>
                  {search ? "No matching rows" : "No data"}
                </td></tr>
              )}
              {pageRows.map((row, ri) => {
                const isSummary = !!row.__summary__;
                return (
                  <tr
                    key={isSummary ? `sum-${ri}` : ri}
                    className={`spg-row${isSummary ? " spg-summary" : ""}${!isSummary && onRowClick ? " spg-click" : ""}`}
                    onClick={() => {
                      if (isSummary || !onRowClick) return;
                      // Cross-filter on the first text column (dates/numbers are skipped, as before)
                      const dimCol = columns.find(c => !numericCols.has(c) && !dateCols.has(c));
                      if (dimCol && row[dimCol]) onRowClick(dimCol, row[dimCol]);
                    }}
                    style={isSummary ? { background: "rgba(31,168,201,0.12)", fontWeight: 700 } : undefined}
                  >
                    {columns.map((col) => {
                      const val   = row[col];
                      const isNum = numericCols.has(col);

                      if (isSummary) {
                        return (
                          <td key={col} style={{
                            ...(isNum ? S.tdNum : S.td),
                            color: "#1FA8C9", fontWeight: 700,
                            borderTop: "2px solid rgba(31,168,201,0.3)",
                          }}>
                            {val !== undefined ? fmtTableNum(val, col) : ""}
                          </td>
                        );
                      }

                      // Date columns: plain text, left-aligned, like Superset
                      if (dateCols.has(col)) {
                        return <td key={col} style={S.td}>{displayDate(col, val)}</td>;
                      }

                      if (isNum) {
                        const cfColor  = evalConditionalColor(val, rulesByCol.get(col));
                        const barColor = (!cfColor && showCellBars) ? cellBarColor(val, colRanges[col]) : null;
                        const bgColor  = cfColor || barColor;
                        const cellStyle = bgColor
                          ? { ...S.tdNum, background: bgColor, color: contrastColor(bgColor), fontWeight: cfColor ? 600 : 400 }
                          : { ...S.tdNum, color: "#1FA8C9" };
                        return <td key={col} style={cellStyle}>{fmtTableNum(val, col)}</td>;
                      }

                      if (isHtmlValue(val) || (HTML_COL_PATTERN.test(col) && val)) {
                        return (
                          <td key={col} style={S.td} onClick={handlePhotoCellClick}
                              dangerouslySetInnerHTML={{ __html: resolveHtmlImageSrc(String(val)) }} />
                        );
                      }
                      return <td key={col} style={S.td}>{val ?? ""}</td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Pager total={total} page={page} pageSize={pageSize} setPage={setPage}
             loading={serverMode && loading} isEstimate={serverMode && srv.isEstimate} />

      {lightbox && (
        <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────
// Client mode (as before):  <PivotTable data={rows} ... />
// Server mode (flat tables): <PivotTable serverRequest={chartDataBody} ... />
//   where chartDataBody is exactly the body ChartCard posts to /chart-data
//   (sliceId, dateFrom, dateTo, timeFilterId, activeFilters, crossFilters).
export default function PivotTable({
  data,
  serverRequest,
  height,
  groupbyRows,
  groupbyColumns,
  metricKeys,
  columnOrder,
  colnames,
  conditionalFormatting,
  showCellBars,
  onRowClick,
}) {
  const isPivot =
    !serverRequest &&
    groupbyColumns?.length > 0 && groupbyRows?.length > 0 && metricKeys?.length > 0;

  // Built ONCE per data change (v4 built it twice on every render)
  const pivot = useMemo(
    () => (isPivot ? buildPivot(data, groupbyRows, groupbyColumns, metricKeys, columnOrder) : null),
    [isPivot, data, groupbyRows, groupbyColumns, metricKeys, columnOrder]
  );

  if (pivot) {
    return (
      <ProperPivotTable
        pivot={pivot}
        height={height}
        conditionalFormatting={conditionalFormatting}
        showCellBars={showCellBars}
        onRowClick={onRowClick}
      />
    );
  }

  return (
    <FlatTable
      data={data}
      serverRequest={serverRequest}
      height={height}
      metricKeys={metricKeys}
      columnOrder={columnOrder}
      colnames={colnames}
      conditionalFormatting={conditionalFormatting}
      showCellBars={showCellBars}
      onRowClick={onRowClick}
    />
  );
}